import {createSlice, isAnyOf, PayloadAction} from '@reduxjs/toolkit';
import {useSelector} from 'react-redux';

import {extendedAPI, useTeamDetail} from 'app/teams/api';

import {BasicTeamSerializedType, TeamSerializedType} from 'sweet-types';

import type {DefaultRootState} from 'react-redux';

export interface TeamsState {
  filters: {
    teamDetailPages: {
      [teamid: string]: {
        [filter: string]: number | string | boolean;
      };
    };
  };
  basicTeamById: {[id: string]: BasicTeamSerializedType};
  teamById: {[id: string]: TeamSerializedType};
}

const initialState: TeamsState = {
  filters: {teamDetailPages: {}},
  basicTeamById: {},
  teamById: {},
};

const teamsSlice = createSlice({
  name: 'teams',
  initialState,
  reducers: {
    setTeamDetailPageFilter: (
      state,
      {
        payload: {teamid, filter},
      }: PayloadAction<{
        teamid: string | number;
        filter: TeamsState['filters']['teamDetailPages'][string];
      }>
    ) => {
      const currentFilter =
        state.filters.teamDetailPages[teamid.toString()] || {};

      state.filters.teamDetailPages[teamid.toString()] = {
        ...currentFilter,
        ...filter,
      };
    },
  },
  extraReducers: (builder) =>
    builder.addMatcher(
      isAnyOf(
        extendedAPI.endpoints.getTeamDetail.matchFulfilled,
        extendedAPI.endpoints.createTeam.matchFulfilled,
        extendedAPI.endpoints.updateTeam.matchFulfilled
      ),
      (state, {payload: team}) => {
        state.teamById[team.id.toString()] = team;
      }
    ),
});

export const teamsReducer = teamsSlice.reducer;

export const {setTeamDetailPageFilter} = teamsSlice.actions;

export const useSweetTeamDetail = (
  teamid: string | number
): ReturnType<typeof useTeamDetail> & {
  team: TeamSerializedType | undefined;
} => {
  const team = useSelector(
    (state: DefaultRootState): TeamSerializedType | undefined =>
      state.teams.teamById[teamid]
  );
  const data = useTeamDetail(teamid, {skip: teamid === ''});
  return {...data, team};
};
